import { Injectable, Injector } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { JsonService } from '../json.service';
import { ApiResponse } from '../../models/api/responses/api-response.model';
import { apiErrors } from '../../config/api/api-errors.config';
import { ErrorCodes } from '../../enums/error/error-codes.enum';
import { AuthenticationService } from '../admin/authentication.service';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  private headers: HttpHeaders = new HttpHeaders().set('Content-Type', 'application/json');
  private requestOption = {headers: this.headers};

  constructor(private jsonService: JsonService, private http: HttpClient, private injector: Injector) {
  }

  async launchAdminApiRequestGet<T extends object>(url: string, responseClassReference?: new () => T): Promise<T> {
    const apiUrlFull: string = await this.getAdminApiUrlFull(url);
    return this.launchAdminApiRequestGetFromFullUrl(apiUrlFull, responseClassReference);
  }

  launchAdminApiRequestGetFromFullUrl<T extends object>(urlFull: string, responseClassReference?: new () => T): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.http.get(urlFull, this.requestOption)
                .subscribe(
                  (response: any) => {

                    const apiResponse: ApiResponse = response as ApiResponse;

                    if (responseClassReference) {
                      const dataResponse: T = this.jsonService.deserializeObject(apiResponse.data, responseClassReference);
                      resolve(dataResponse);
                    } else {
                      resolve(apiResponse.data);
                    }
                  }, error => {
                  this.handleHttpResponseError(error).then(
                    (errorMessage: string) => {
                      reject(errorMessage);
                  });
                  }
                );
    });
  }

  async launchAdminApiRequestGetDownload(url: string): Promise<void> {
    const apiUrlFull: string = await this.getAdminApiUrlFull(url);
    return this.launchAdminApiRequestGetDownloadFromFullUrl(apiUrlFull);
  }

  launchAdminApiRequestGetDownloadFromFullUrl(urlFull: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.http.get(urlFull, {responseType: 'blob'})
                .subscribe(
                  response => {
                    resolve(response);
                  }, error => {
                    reject();
                  }
                );
    });
  }

  async launchAdminApiRequestPostDownload(url: string, body: any): Promise<void> {
    const apiUrlFull: string = await this.getAdminApiUrlFull(url);
    return this.launchAdminApiRequestPostDownloadFromFullUrl(apiUrlFull, body);
  }
  
  launchAdminApiRequestPostDownloadFromFullUrl(urlFull: string, body: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.http.post(urlFull, body, {responseType: 'blob'})
                .subscribe(
                  response => {
                    resolve(response);
                  }, error => {
                    reject();
                  }
                );
    });
  }

  async launchAdminApiRequestPost<T extends object>(url: string, body: any, responseClassReference?: new () => T): Promise<T> {
    const apiUrlFull: string = await this.getAdminApiUrlFull(url);
    return this.launchAdminApiRequestPostFromFullUrl(apiUrlFull, body, responseClassReference);
  }

  async launchAdminApiRequestPostFile(url: string, formData: FormData): Promise<void> {
    const apiUrlFull: string = await this.getAdminApiUrlFull(url);
    return new Promise<void>((resolve, reject) => {
      this.http.post(apiUrlFull, formData).subscribe({
        next: () => { resolve(); },
        error: error => { this.handleHttpResponseError(error).then(msg => reject(msg)); }
      });
    });
  }

  launchAdminApiRequestPostFromFullUrl<T extends object>(urlFull: string, body: any, responseClassReference?: new () => T): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        this.http.post(urlFull, body, this.requestOption)
        .subscribe(
          response => {
            const apiResponse: ApiResponse = response as ApiResponse;

            if (responseClassReference) {
              const dataResponse: T = this.jsonService.deserializeObject(apiResponse.data, responseClassReference);
              resolve(dataResponse);
            } else {
              resolve(apiResponse.data as T);
            }
          },
          error => {
            this.handleHttpResponseError(error).then(
              (errorMessage: string) => {
                reject(errorMessage);
            });
          }
        );
    });
  }

  // Error handling
  private handleHttpResponseError(error: HttpErrorResponse): Promise<string> {
    return new Promise<any>((resolve, reject) => {
      if (error.error instanceof Object) {
        const apiResponse: ApiResponse = error.error as ApiResponse;

        let errorCode: ErrorCodes | undefined;
        if (apiResponse.error)
        {
          errorCode = apiResponse.error.code;
        }

        if (errorCode === ErrorCodes.InvalidUserSession) {
          this.injector.get(AuthenticationService).logout();
        }

        resolve(errorCode);

        console.error(errorCode, error);
      } else {
        console.error(error);
        resolve(error.message);
      }
    });
  }

  private getAdminApiUrlFull(urlEnd: string): string {
    const urlFinal: string = environment.apiAdminBaseUrl + urlEnd;

    return urlFinal;
  }

  private getApiErrorMessageFromCode(errorCode: number): string {
    for (let i = 0; i < apiErrors.length; i++) {
      if (errorCode === apiErrors[i].code) {

        return apiErrors[i].message;
      }
    }

    return 'Generic api error';
  }
}
